import React, { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Camera, FileCheck, Loader2, Trash2, UploadCloud, UsersRound,
} from "lucide-react";
import { DocPreview, docFileUrl } from "@/components/DocPreview";

const INPUT_CLASS = [
  "w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm",
  "focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#27AE60]",
].join(" ");

const PARENT_STATUS_OPTIONS = [
  ["hidup", "Hidup"],
  ["meninggal", "Meninggal"],
];

const JOB_OPTIONS = [
  "PNS",
  "TNI / Polri",
  "Pegawai Swasta",
  "Wiraswasta",
  "Buruh",
  "Petani / Nelayan",
  "Ibu Rumah Tangga",
  "Tidak Bekerja",
  "Pensiunan",
  "Lainnya",
];

const RELATION_OPTIONS = [
  "Kakak",
  "Adik",
  "Kakek / Nenek",
  "Paman / Bibi",
  "Saudara Lainnya",
];

const EDUCATION_OPTIONS = [
  "Tidak / Belum Sekolah",
  "SD",
  "SMP",
  "SMA / SMK",
  "D1 / D2 / D3",
  "D4 / S1",
  "S2",
  "S3",
];

const ELECTRICITY_OPTIONS = [
  "450 VA",
  "900 VA",
  "1.300 VA",
  "2.200 VA",
  "3.500 VA",
  "> 3.500 VA",
];

const HOME_OWNERSHIP_OPTIONS = [
  "Rumah Sendiri (Lunas / Warisan)",
  "Rumah Sendiri (Masih Cicil KPR)",
  "Kontrak / Sewa per Tahun / per Bulan",
  "Menumpang di Rumah Keluarga / Kerabat",
];

const HOME_CONDITION_OPTIONS = [
  "Permanen (Tembok layak, lantai keramik)",
  "Semi Permanen (Dinding separuh kayu/bambu)",
  "Non-Permanen / Memprihatinkan",
];

const INCOME_RANGE_FLAG = "> Rp 10.000.000";

const HOUSE_PHOTO_MAX_SIZE = 3 * 1024 * 1024;
const HOUSE_PHOTO_DOCS = [
  {
    docType: "Foto Tampak Depan Rumah",
    title: "Foto Tampak Depan Rumah (Menyeluruh)",
    desc: "Ambil dari luar jalan/halaman sehingga terlihat keseluruhan bagian depan rumah, termasuk dinding, pintu, dan atap.",
    testId: "house-front",
  },
  {
    docType: "Foto Ruang Tamu / Ruang Keluarga",
    title: "Foto Ruang Tamu / Ruang Keluarga",
    desc: "Ambil dari salah satu sisi ruangan utama tempat keluarga berkumpul.",
    testId: "house-living-room",
  },
];

const INCOME_RANGE_OPTIONS = [
  "< Rp 1.500.000",
  "Rp 1.500.000 – Rp 3.000.000",
  "Rp 3.000.000 – Rp 6.000.000",
  "Rp 6.000.000 – Rp 10.000.000",
  INCOME_RANGE_FLAG,
];

const PARENT_FIELDS = [
  ["name", "Nama Lengkap"],
  ["nik", "NIK"],
  ["status", "Status Orang Tua"],
  ["phone", "Nomor HP"],
  ["job", "Pekerjaan"],
  ["income", "Penghasilan / Bulan"],
];

const MEMBER_FIELDS = [
  ["name", "Nama Lengkap"],
  ["nik", "NIK"],
  ["relationship", "Hubungan"],
  ["education", "Pendidikan Terakhir"],
  ["job", "Pekerjaan"],
  ["income", "Penghasilan / Bulan"],
];

const EMPTY_PARENT = {
  name: "",
  nik: "",
  status: "",
  phone: "",
  job: "",
  income: "",
};

const EMPTY_MEMBER = {
  name: "",
  nik: "",
  relationship: "",
  education: "",
  job: "",
  income: "",
};

const hasValue = (value) => String(value ?? "").trim() !== "";

export const parseFamilyCount = (value) => {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(parsed, 15));
};

export const normalizeFamilyState = (family) => {
  const source = family && typeof family === "object" ? family : {};
  const count = parseFamilyCount(source.otherMemberCount);
  const members = Array.isArray(source.otherMembers) ? source.otherMembers : [];
  return {
    father: { ...EMPTY_PARENT, ...(source.father || {}) },
    mother: { ...EMPTY_PARENT, ...(source.mother || {}) },
    otherMemberCount: source.otherMemberCount ?? "",
    otherMembers: Array.from(
      { length: count },
      (_, index) => ({ ...EMPTY_MEMBER, ...(members[index] || {}) }),
    ),
    electricityPower: source.electricityPower || "",
    homeOwnership: source.homeOwnership || "",
    homeCondition: source.homeCondition || "",
    incomeRange: source.incomeRange || "",
  };
};

export const mergeScannedProfile = (previous, mapped) => {
  const next = { ...previous, ...mapped };
  if (mapped.family) {
    next.family = normalizeFamilyState({ ...previous.family, ...mapped.family });
  }
  return next;
};

export const getMissingFamilyFields = (data = {}) => {
  const family = normalizeFamilyState(data.family);
  const missing = [];
  [
    ["father", "Ayah"],
    ["mother", "Ibu"],
  ].forEach(([parentKey, parentLabel]) => {
    PARENT_FIELDS.forEach(([key, label]) => {
      if (!hasValue(family[parentKey][key])) {
        missing.push({ k: `family.${parentKey}.${key}`, l: `${label} ${parentLabel}` });
      }
    });
  });
  if (!hasValue(family.otherMemberCount)) {
    missing.push({ k: "family.otherMemberCount", l: "Jumlah Anggota Keluarga Lain" });
  }
  family.otherMembers.forEach((member, index) => {
    MEMBER_FIELDS.forEach(([key, label]) => {
      if (!hasValue(member[key])) {
        missing.push({
          k: `family.otherMembers.${index}.${key}`,
          l: `${label} Anggota Keluarga ${index + 1}`,
        });
      }
    });
  });
  if (!hasValue(family.electricityPower)) {
    missing.push({ k: "family.electricityPower", l: "Daya Listrik Rumah" });
  }
  if (!hasValue(family.homeOwnership)) {
    missing.push({ k: "family.homeOwnership", l: "Status Kepemilikan Rumah" });
  }
  if (!hasValue(family.incomeRange)) {
    missing.push({ k: "family.incomeRange", l: "Estimasi Penghasilan Rata-Rata per Bulan" });
  }
  return missing;
};

export const getFamilyRequiredFieldCount = (data = {}) => {
  const count = parseFamilyCount(data.family?.otherMemberCount);
  return (PARENT_FIELDS.length * 2) + 4 + (MEMBER_FIELDS.length * count);
};

const formatRupiah = (value) => String(value || "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");

function FieldLabel({ children, htmlFor, optional = false }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold text-[#1F2937]">
      {children}{" "}
      {optional ? (
        <span className="text-xs font-normal text-[#9CA3AF]">(Opsional)</span>
      ) : (
        <span className="text-[#DC2626]">*</span>
      )}
    </label>
  );
}

function ParentForm({ id, label, value, onChange }) {
  const prefix = `family-${id}`;
  const setField = (key, nextValue) => onChange({ ...value, [key]: nextValue });
  return (
    <div
      className="rounded-xl border border-gray-100 bg-[#F9FAFB] p-5"
      data-testid={`${prefix}-card`}
    >
      <h4 className="font-display text-base font-bold text-[#1F2937]">Data {label}</h4>
      <div className="mt-4 grid gap-x-5 gap-y-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor={`${prefix}-name`}>Nama Lengkap</FieldLabel>
          <input
            id={`${prefix}-name`}
            value={value.name}
            onChange={(event) => setField("name", event.target.value)}
            placeholder={`Nama lengkap ${label.toLowerCase()}`}
            data-testid={`${prefix}-name`}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-nik`}>NIK</FieldLabel>
          <input
            id={`${prefix}-nik`}
            inputMode="numeric"
            maxLength={16}
            value={value.nik}
            onChange={(event) => setField("nik", event.target.value.replace(/\D/g, ""))}
            placeholder="16 digit NIK"
            data-testid={`${prefix}-nik`}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-status`}>Status Orang Tua</FieldLabel>
          <select
            id={`${prefix}-status`}
            value={value.status}
            onChange={(event) => setField("status", event.target.value)}
            data-testid={`${prefix}-status`}
            className={`${INPUT_CLASS} bg-white`}
          >
            <option value="">Pilih status...</option>
            {PARENT_STATUS_OPTIONS.map(([optionValue, optionLabel]) => (
              <option key={optionValue} value={optionValue}>{optionLabel}</option>
            ))}
          </select>
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-phone`}>Nomor HP</FieldLabel>
          <input
            id={`${prefix}-phone`}
            inputMode="tel"
            value={value.phone}
            onChange={(event) => setField("phone", event.target.value)}
            placeholder="Contoh: 08123456789"
            data-testid={`${prefix}-phone`}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-job`}>Pekerjaan</FieldLabel>
          <select
            id={`${prefix}-job`}
            value={value.job}
            onChange={(event) => setField("job", event.target.value)}
            data-testid={`${prefix}-job`}
            className={`${INPUT_CLASS} bg-white`}
          >
            <option value="">Pilih pekerjaan...</option>
            {JOB_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-income`}>Penghasilan / Bulan</FieldLabel>
          <div className="relative">
            <span
              className={[
                "pointer-events-none absolute inset-y-0 left-0 flex items-center px-4 text-sm",
                "font-bold text-[#0B6B3A]",
              ].join(" ")}
            >
              Rp
            </span>
            <input
              id={`${prefix}-income`}
              inputMode="numeric"
              value={formatRupiah(value.income)}
              onChange={(event) => {
                setField("income", event.target.value.replace(/\D/g, ""));
              }}
              placeholder="0"
              data-testid={`${prefix}-income`}
              className={`${INPUT_CLASS} pl-11`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function OtherMemberForm({ index, value, onChange }) {
  const prefix = `family-member-${index}`;
  const setField = (key, nextValue) => onChange({ ...value, [key]: nextValue });
  return (
    <div className="border border-gray-100 bg-white p-4" data-testid={`${prefix}-card`}>
      <p className="mb-4 text-sm font-bold text-[#1F2937]">Anggota Keluarga {index + 1}</p>
      <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <FieldLabel htmlFor={`${prefix}-name`}>Nama Lengkap</FieldLabel>
          <input
            id={`${prefix}-name`}
            value={value.name}
            onChange={(event) => setField("name", event.target.value)}
            data-testid={`${prefix}-name`}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-nik`}>NIK</FieldLabel>
          <input
            id={`${prefix}-nik`}
            inputMode="numeric"
            maxLength={16}
            value={value.nik}
            onChange={(event) => setField("nik", event.target.value.replace(/\D/g, ""))}
            data-testid={`${prefix}-nik`}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-relationship`}>Hubungan</FieldLabel>
          <select
            id={`${prefix}-relationship`}
            value={value.relationship}
            onChange={(event) => setField("relationship", event.target.value)}
            data-testid={`${prefix}-relationship`}
            className={`${INPUT_CLASS} bg-white`}
          >
            <option value="">Pilih hubungan...</option>
            {RELATION_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-education`}>Pendidikan Terakhir</FieldLabel>
          <select
            id={`${prefix}-education`}
            value={value.education}
            onChange={(event) => setField("education", event.target.value)}
            data-testid={`${prefix}-education`}
            className={`${INPUT_CLASS} bg-white`}
          >
            <option value="">Pilih pendidikan...</option>
            {EDUCATION_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-job`}>Pekerjaan</FieldLabel>
          <select
            id={`${prefix}-job`}
            value={value.job}
            onChange={(event) => setField("job", event.target.value)}
            data-testid={`${prefix}-job`}
            className={`${INPUT_CLASS} bg-white`}
          >
            <option value="">Pilih pekerjaan...</option>
            {JOB_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel htmlFor={`${prefix}-income`}>Penghasilan / Bulan</FieldLabel>
          <div className="relative">
            <span
              className={[
                "pointer-events-none absolute inset-y-0 left-0 flex items-center px-4 text-sm",
                "font-bold text-[#0B6B3A]",
              ].join(" ")}
            >
              Rp
            </span>
            <input
              id={`${prefix}-income`}
              inputMode="numeric"
              value={formatRupiah(value.income)}
              onChange={(event) => {
                setField("income", event.target.value.replace(/\D/g, ""));
              }}
              placeholder="0"
              data-testid={`${prefix}-income`}
              className={`${INPUT_CLASS} pl-11`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function HousePhotoUploader({ config, docs, locked, uploadDoc, deleteDoc }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const doc = (docs || []).find((item) => item.doc_type === config.docType);
  const isImage = doc && (doc.content_type || "").startsWith("image/");
  const previewUrl = isImage ? docFileUrl(doc) : null;

  const onFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png", "pdf"].includes(ext)) {
      return toast.error("Format harus JPG, PNG, atau PDF.");
    }
    if (file.size > HOUSE_PHOTO_MAX_SIZE) {
      return toast.error("Ukuran file maksimal 3 MB.");
    }
    setBusy(true);
    await uploadDoc(config.docType, file);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div
      className="rounded-xl border border-gray-100 bg-white p-4"
      data-testid={`${config.testId}-card`}
    >
      <div className="flex flex-col gap-4 sm:flex-row">
        <button
          type="button"
          onClick={() => doc && setOpen(true)}
          disabled={!doc}
          className="flex h-32 w-full shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-gray-200 bg-[#F9FAFB] sm:w-44"
          data-testid={`${config.testId}-preview`}
        >
          {previewUrl ? (
            <img src={previewUrl} alt={config.title} className="h-full w-full cursor-zoom-in object-cover" />
          ) : doc ? (
            <span className="flex flex-col items-center gap-1 text-[#0B6B3A]">
              <FileCheck className="h-8 w-8" />
              <span className="px-2 text-[11px] font-semibold">Lihat berkas PDF</span>
            </span>
          ) : (
            <Camera className="h-10 w-10 text-gray-300" />
          )}
        </button>
        {open && doc && <DocPreview doc={doc} onClose={() => setOpen(false)} />}
        <div className="min-w-0 flex-1">
          <h5 className="font-display text-sm font-bold text-[#1F2937]">{config.title}</h5>
          <p className="mt-1 text-xs text-[#6B7280]">{config.desc}</p>
          <p className="mt-2 text-xs text-[#9CA3AF]">
            Format: JPG, PNG, atau PDF. Ukuran maksimal 3 MB per file.
          </p>
          {doc && (
            <p className="mt-2 break-all text-xs font-semibold text-[#0B6B3A]" data-testid={`${config.testId}-filename`}>
              File saat ini: {doc.original_filename}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy || locked}
              data-testid={`${config.testId}-upload-btn`}
              className="flex items-center gap-2 rounded-xl bg-[#27AE60] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A] disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              {locked ? "Terkunci" : doc ? "Ganti Foto" : "Unggah Foto"}
            </button>
            {doc && !locked && (
              <button
                type="button"
                onClick={() => deleteDoc(doc.id)}
                data-testid={`${config.testId}-delete-btn`}
                className="flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-bold text-[#DC2626] transition-colors hover:bg-[#FEE2E2]"
              >
                <Trash2 className="h-4 w-4" /> Hapus
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            disabled={locked}
            className="hidden"
            accept=".jpg,.jpeg,.png,.pdf"
            data-testid={`${config.testId}-file-input`}
            onChange={(event) => onFile(event.target.files[0])}
          />
        </div>
      </div>
    </div>
  );
}

export default function FamilyProfileSection({ data, setData, docs, locked, uploadDoc, deleteDoc }) {
  const family = normalizeFamilyState(data.family);

  const updateFamily = (updater) => {
    setData((previous) => ({
      ...previous,
      family: updater(normalizeFamilyState(previous.family)),
    }));
  };

  const setParent = (parentKey, parent) => updateFamily((current) => ({
    ...current,
    [parentKey]: parent,
  }));

  const setMember = (index, member) => updateFamily((current) => ({
    ...current,
    otherMembers: current.otherMembers.map((item, itemIndex) => (
      itemIndex === index ? member : item
    )),
  }));

  const setMemberCount = (rawValue) => {
    const count = parseFamilyCount(rawValue);
    updateFamily((current) => ({
      ...current,
      otherMemberCount: rawValue,
      otherMembers: Array.from(
        { length: count },
        (_, index) => ({ ...EMPTY_MEMBER, ...(current.otherMembers[index] || {}) }),
      ),
    }));
  };

  return (
    <div className="space-y-6" data-testid="family-profile-section">
      <div className="flex items-start gap-3 border-l-4 border-[#27AE60] bg-[#F0FBF5] px-4 py-4">
        <UsersRound className="mt-0.5 h-5 w-5 shrink-0 text-[#0B6B3A]" />
        <div>
          <h3 className="font-display text-lg font-bold text-[#1F2937]">Data Keluarga</h3>
          <p className="mt-1 text-sm text-[#6B7280]">
            Isi data keluarga berdasarkan informasi terakhir yang diketahui. Semua kolom bertanda
            bintang wajib diisi.
          </p>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <ParentForm
          id="father"
          label="Ayah"
          value={family.father}
          onChange={(parent) => setParent("father", parent)}
        />
        <ParentForm
          id="mother"
          label="Ibu"
          value={family.mother}
          onChange={(parent) => setParent("mother", parent)}
        />
      </div>

      <div className="bg-white p-6 shadow-[0_4px_20px_-2px_rgba(39,174,96,0.05)]">
        <div className="max-w-sm">
          <FieldLabel htmlFor="family-other-member-count">Jumlah Anggota Keluarga Lain</FieldLabel>
          <input
            id="family-other-member-count"
            type="number"
            min="0"
            max="15"
            inputMode="numeric"
            value={family.otherMemberCount}
            onChange={(event) => setMemberCount(event.target.value.replace(/\D/g, ""))}
            placeholder="Contoh: 2"
            data-testid="family-other-member-count"
            className={INPUT_CLASS}
          />
          <p className="mt-1 text-xs text-[#6B7280]" data-testid="family-count-ocr-hint">Terisi otomatis setelah KK terbaca. Maksimal 15 anggota selain ayah dan ibu, termasuk pendaftar. Periksa dan koreksi bila perlu.</p>
        </div>

        {family.otherMembers.length > 0 && (
          <div className="mt-6 space-y-4" data-testid="family-other-members-list">
            <h4 className="font-display text-base font-bold text-[#1F2937]">
              Anggota Keluarga Lain
            </h4>
            {family.otherMembers.map((member, index) => (
              <OtherMemberForm
                key={index}
                index={index}
                value={member}
                onChange={(nextMember) => setMember(index, nextMember)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="bg-white p-6 shadow-[0_4px_20px_-2px_rgba(39,174,96,0.05)]">
        <h4 className="font-display text-base font-bold text-[#1F2937]">
          Informasi Rumah &amp; Ekonomi Keluarga
        </h4>
        <div className="mt-4 grid gap-x-5 gap-y-4 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="family-home-ownership">Status Kepemilikan Rumah</FieldLabel>
            <select
              id="family-home-ownership"
              value={family.homeOwnership}
              onChange={(event) => updateFamily((current) => ({
                ...current,
                homeOwnership: event.target.value,
              }))}
              data-testid="family-home-ownership"
              className={`${INPUT_CLASS} bg-white`}
            >
              <option value="">Pilih status kepemilikan...</option>
              {HOME_OWNERSHIP_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel htmlFor="family-home-condition" optional>Kondisi Fisik Rumah</FieldLabel>
            <select
              id="family-home-condition"
              value={family.homeCondition}
              onChange={(event) => updateFamily((current) => ({
                ...current,
                homeCondition: event.target.value,
              }))}
              data-testid="family-home-condition"
              className={`${INPUT_CLASS} bg-white`}
            >
              <option value="">Pilih kondisi rumah...</option>
              {HOME_CONDITION_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel htmlFor="family-income-range">
              Estimasi Penghasilan Rata-Rata per Bulan
            </FieldLabel>
            <select
              id="family-income-range"
              value={family.incomeRange}
              onChange={(event) => updateFamily((current) => ({
                ...current,
                incomeRange: event.target.value,
              }))}
              data-testid="family-income-range"
              className={`${INPUT_CLASS} bg-white`}
            >
              <option value="">Pilih rentang penghasilan...</option>
              {INCOME_RANGE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel htmlFor="family-electricity-power">Daya Listrik Rumah</FieldLabel>
            <select
              id="family-electricity-power"
              value={family.electricityPower}
              onChange={(event) => updateFamily((current) => ({
                ...current,
                electricityPower: event.target.value,
              }))}
              data-testid="family-electricity-power"
              className={`${INPUT_CLASS} bg-white`}
            >
              <option value="">Pilih daya listrik...</option>
              {ELECTRICITY_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div
        className="bg-white p-6 shadow-[0_4px_20px_-2px_rgba(39,174,96,0.05)]"
        data-testid="family-house-photos"
      >
        <h4 className="font-display text-base font-bold text-[#1F2937]">Foto Kondisi Rumah</h4>
        <p className="mt-1 text-sm text-[#6B7280]">
          Unggah foto pendukung kondisi rumah sebagai bahan verifikasi.
        </p>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {HOUSE_PHOTO_DOCS.map((config) => (
            <HousePhotoUploader
              key={config.docType}
              config={config}
              docs={docs}
              locked={locked}
              uploadDoc={uploadDoc}
              deleteDoc={deleteDoc}
            />
          ))}
        </div>
      </div>
    </div>
  );
}